// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/pages/Pricing.tsx
// Version: v1.1.0
// Pricing-Seite: Eingeloggte User → Stripe Checkout (bestehender Tenant), Neukunden → /signup

import { useNavigate } from "react-router-dom";
import { useState } from "react";

const plans = [
  {
    name: "Standard",
    price: "29",
    desc: "Für kleine Teams und Einsteiger",
    features: [
      "Bis zu 10 Nutzer",
      "Zeiterfassung & Projekte",
      "Due Diligence",
      "E-Mail-Support",
    ],
    cta: "Kostenlos starten",
    highlight: false,
    plan: "standard",
  },
  {
    name: "Pro",
    price: "79",
    desc: "Für wachsende Unternehmen",
    features: [
      "Bis zu 50 Nutzer",
      "Alle Module inkl. Planstar",
      "Fotodokumentation",
      "Prioritäts-Support",
    ],
    cta: "Jetzt starten",
    highlight: true,
    plan: "pro",
  },
  {
    name: "Enterprise",
    price: "199",
    desc: "Für große Organisationen",
    features: [
      "Unbegrenzte Nutzer",
      "Alle Module",
      "SLA & dedizierter Support",
      "On-Premise Option",
    ],
    cta: "Kontakt aufnehmen",
    highlight: false,
    plan: "enterprise",
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);

  const handleCta = async (plan: string) => {
    if (plan === "enterprise") {
      navigate("/contact");
      return;
    }

    setLoading(plan);
    try {
      // Prüfen ob User eingeloggt ist
      const authRes = await fetch("/system/api/v1/auth/check", { credentials: "include" });
      const authData = await authRes.json();

      if (authData.authenticated) {
        // Eingeloggt → Stripe Checkout für bestehenden Tenant
        const checkoutRes = await fetch("/system/api/v1/billing/change-plan", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ plan_name: plan }),
        });
        const checkoutData = await checkoutRes.json();
        if (checkoutData.checkout_url) {
          window.location.href = checkoutData.checkout_url;
        } else if (checkoutData.message) {
          window.location.href = "/myest/settings";
        } else {
          alert(checkoutData.detail || "Fehler beim Checkout");
        }
      } else {
        // Nicht eingeloggt → normaler Signup-Flow
        navigate(`/signup?plan=${plan}`);
      }
    } catch {
      navigate(`/signup?plan=${plan}`);
    } finally {
      setLoading(null);
    }
  };

  return (
    <section className="min-h-screen bg-white py-24 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Einfache, transparente Preise
          </h1>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto">
            14 Tage kostenlos testen — keine Kreditkarte erforderlich.
          </p>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl border p-8 flex flex-col ${
                plan.highlight
                  ? "border-indigo-600 shadow-xl bg-indigo-50"
                  : "border-gray-200 bg-white shadow-sm"
              }`}
            >
              {plan.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-indigo-600 text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide">
                    Empfohlen
                  </span>
                </div>
              )}

              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900">{plan.name}</h2>
                <p className="text-gray-500 text-sm mt-1">{plan.desc}</p>
              </div>

              <div className="mb-8">
                <span className="text-5xl font-extrabold text-gray-900">
                  €{plan.price}
                </span>
                <span className="text-gray-400 ml-2">/ Monat</span>
              </div>

              <ul className="space-y-3 mb-10 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-gray-700 text-sm">
                    <svg className="w-5 h-5 text-indigo-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleCta(plan.plan)}
                disabled={loading === plan.plan}
                className={`w-full py-3 rounded-xl font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-wait ${
                  plan.highlight
                    ? "bg-indigo-600 text-white hover:bg-indigo-700"
                    : "bg-gray-900 text-white hover:bg-gray-700"
                }`}
              >
                {loading === plan.plan ? "..." : plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Hinweis */}
        <p className="text-center text-gray-400 text-sm mt-12">
          Alle Preise zzgl. MwSt. · Monatlich kündbar · DSGVO-konform · Server in Deutschland
        </p>
      </div>
    </section>
  );
}
